import { SharedSpinner } from '../Loading/SharedSpinner';
import { InboxOutlined, MobileOutlined } from '@ant-design/icons';
import { AppConsts, L } from '@lib';
import { NotificationSubscription } from '@models';
import { DocumentService, InventoryService } from '@services';
import { Typography, Upload, message } from 'antd';
import QRCode from 'react-qr-code';
import React, { useEffect, useRef, useState } from 'react';

declare const abp: any;

const { Text } = Typography;

export interface CapturedCreditNote {
  documentId: string;
  fileName: string;
  /** Data URL of a locally uploaded file, so it can be previewed before it is attached. */
  previewSrc?: string;
  contentType?: string;
}

interface IProps {
  /** Drives the capture-session lifecycle — pass the open state of the host modal/drawer. */
  active: boolean;
  fileName?: string;
  onCaptured: (captured: CapturedCreditNote) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Captures a single credit note — direct upload or phone QR capture — and reports the resulting
 * documentId to the parent. Hosted by CreditNoteModal and, inline, by the movement details drawer.
 */
export const CreditNoteCapture: React.FC<IProps> = ({ active, fileName, onCaptured, onUploadingChange }) => {
  const [captureToken, setCaptureToken] = useState<string | null>(null);
  const captureTokenRef = useRef<string | null>(null);
  captureTokenRef.current = captureToken;
  // The notification handler is registered once per capture session, so read the callback through a
  // ref to avoid holding on to the first render's closure.
  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;

  useEffect(() => {
    if (!active) {
      setCaptureToken(null);
      return undefined;
    }

    let isActive = true;

    const handler = async (userNotification: any) => {
      const name = userNotification?.notification?.notificationName;
      if (name !== NotificationSubscription.InventoryCaptureCompleted) return;
      const data = userNotification?.notification?.data?.properties;
      if (!data?.documentId || data.token !== captureTokenRef.current) return;
      let capturedName = 'Mobile capture';
      try {
        const file = await DocumentService.get({ id: data.documentId });
        capturedName = file.name || capturedName;
      } catch {
        // Keep the fallback name — the document itself was created successfully.
      }
      onCapturedRef.current({ documentId: data.documentId, fileName: capturedName });
      message.success(L('Credit note received from mobile!'));
    };
    abp.event.on('abp.notifications.received', handler);

    InventoryService.createCaptureSession()
      .then((session) => {
        if (isActive) setCaptureToken(session.token);
      })
      .catch(() => console.error('Failed to create capture session'));

    return () => {
      isActive = false;
      abp.event.off('abp.notifications.received', handler);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Returning false stops antd's default upload — we upload manually to obtain a documentId.
  const handleUpload = async (file: File) => {
    onUploadingChange?.(true);
    try {
      const content = await readAsDataUrl(file);
      const doc = await InventoryService.uploadInvoice({
        name: file.name,
        content,
        contentType: file.type,
      });
      // Without an id there is nothing to attach, so don't report a capture at all.
      if (!doc?.id) throw new Error('Upload returned no document id');
      onCapturedRef.current({
        documentId: doc.id,
        fileName: file.name,
        previewSrc: content,
        contentType: file.type,
      });
      message.success(L('Credit note uploaded'));
    } catch (e) {
      console.error(e);
      message.error(L('Failed to upload credit note'));
    } finally {
      onUploadingChange?.(false);
    }
    return false;
  };

  return (
    <>
      <Upload.Dragger
        accept="image/*,.pdf"
        showUploadList={false}
        multiple={false}
        beforeUpload={handleUpload}
      >
        <p style={{ marginBottom: 4 }}>
          <InboxOutlined style={{ fontSize: 22, color: '#13c2c2' }} />
        </p>
        <p style={{ margin: 0 }}>{fileName || L('Click or drag credit note to upload')}</p>
      </Upload.Dragger>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0' }}>
        <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {L('OR')}
        </Text>
        <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '12px 16px',
          background: '#fafafa',
          borderRadius: 8,
          border: '1px solid #f0f0f0',
        }}
      >
        {captureToken ? (
          <div
            style={{
              background: '#fff',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #e8e8e8',
              flexShrink: 0,
            }}
          >
            <QRCode value={`${AppConsts.appBaseUrl}/capture/${captureToken}`} size={100} level="M" />
          </div>
        ) : (
          <SharedSpinner size="small" loading />
        )}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <MobileOutlined style={{ fontSize: 16, color: '#722ed1' }} />
            <Text strong>{L('Scan with your phone')}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {L(
              'Scan the QR code to take a photo from your phone. The credit note will appear here automatically.'
            )}
          </Text>
        </div>
      </div>
    </>
  );
};

export default CreditNoteCapture;
